import { z } from 'zod';
import { zodEditStateEnum, editStateEnum } from './data-model.enums';
import { Paragraphs } from './data-model.paragraphs';
import { Paragraph, paragraphSchema } from './data-model.paragraph';
import { CapicheLoggerCore } from '@capiche/ts-logger-core';

const logger: CapicheLoggerCore = new CapicheLoggerCore('document:', false);

// TODO: are these notes useful?
//
// handleUserInput
// - user input can change 0 or more characters,tokens,sentences,paragraphs in the document
// - any single change will have claery identifyable unchanged and altererd portions
// - each chnage should in seqId to ensure that the most recent user input state can be identified.
// - any user initiated chaneg should overwrite the fewest possible number of elements in the document
// - all unchanged parts should be retained in order to preserve any existing suggestions or previously make replacements
// in most curcumstances changes will be small and incremental either adding new tokens to the end of an existing sentence etc..
//    .. or inserting/removing tokens/chars mid sentece/token
// changes to the document may well change the length of elements in the document
//   - this implies that all following elements should be updated/re-indexed accordingly
//   - this only applies to parts of the document that follow the changed element
//
// handleCapicheResponse
// - capiche responses will typically contain a sub-set of the document that has been changed
// - they do not act on the document text directly and so will not change the input or any indexes
// - these contain updated state and suggestions for the elements that have been looked up
// - the response will contain a seqId that repreents the userInput version that was used in the look-up call
// - the response will contain a srcId that represents the document that was looked up
//
// handleUserReplacement
// - this is called when the user selects a suggestion from the suggestions list
// - this will typically replace a single token or sentence in the document
// - making a replacement will change the state of the element that was replaced
// - making a replacement will act on the input, modifying the input string << TODO: is this true?
//   ?? .. or will it simply make a replacement in the input field; triggering handleUserInput ??
// - making a replacement may change the length of the input string and require re-indexing of the document

// Schema aligned with established pattern
export const documentSchema = z.strictObject({
  // optional source ID for tracking
  srcId: z.string().optional(),
  // sequence ID for versioning
  seqId: z.number().int().min(0).optional(),
  // the document text as provided by the user, including original case and any/all punctuation etc.
  input: z.string().optional(),
  // the index of the document in the host application
  // TODO: check: prose-mirror seems to have an offset of two?
  index: z.number().int().min(0).optional(),
  // the length of the document in the input string
  length: z.number().int().min(0).optional(),
  // the state of the document in the editor
  editState: zodEditStateEnum,

  // the spelling state of the sentence in the editor
  // the state refects the most severe state of any of the tokens in the sentence
  // misspelled > contextual > correct
  // spellingState: zodSpellingStateEnum, // TODO: this should be added

  // the paragraphs comprising the document
  paragraphs: z.array(paragraphSchema),
  // TODO: the corrected document text as provided by the LLM, including original case and any/all punctuation etc.
  suggested: z.string().optional(),
});

export type documentDTO = z.infer<typeof documentSchema>;

export class Document {
  static dtoSchema = documentSchema;

  constructor(public readonly dto: documentDTO) {
    logger.log('constructor:', dto);
  }

  // Factory: from DTO
  static fromDTO(dto: documentDTO): Document {
    logger.log('fromDTO:', dto);
    Document.dtoSchema.parse(dto);
    return new Document(dto);
  }

  // Factory: from raw string
  static fromString(text: string, seqId?: number, srcId?: string): Document {
    logger.log('fromString:', text, seqId, srcId);
    const paragraphs = Paragraphs.fromString(text, seqId, srcId);

    return new Document({
      srcId,
      seqId,
      input: text,
      suggested: undefined,
      index: 0, // TODO: since this is the top level should this be handled differently from paragraphs etc.. (prose-mirror has an offset of two?)
      length: text.length,
      editState: editStateEnum.new,
      paragraphs: paragraphs.toDTOs(),
    });
  }

  // Serialization
  toDTO(): documentDTO {
    logger.log('toDTO:', this.dto);
    return this.dto;
  }

  // Business logic
  // called when the user make a change to the input field (type/paste/delete etc..)
  handleUserInput(input: string): Document {
    logger.log('handleUserInput:', this.dto, input);
    const inputSeqId: number = (this.dto.seqId ?? 0) + 1;
    const inputParagraphs: Paragraphs = Paragraphs.fromString(input, inputSeqId, this.dto.srcId);

    // somehow compare inputParagraphs with this.dto.paragraphs
    // for each paragraph: if there are no changes, use this.dto.paragraphs
    // if there are changes, ... call handleUserInput on the paragraph??
    // build outputParagraphs from this logic
    const outputParagraphs: Paragraphs = inputParagraphs;

    return new Document({
      ...this.dto,
      seqId: inputSeqId,
      input: input,
      length: input.length,
      editState: editStateEnum.pending, // TODO: not sure this is right - CONFIRM or CHANGE
      paragraphs: outputParagraphs.toDTOs(),
    });
  }

  // previous update method from element-state-store.ts - THIS IS PROBABLY NOT RIGHT
  //    update(elId: string, newContent: string): ElementState | undefined {
  //    // IMPORTANT: This is for user-initiated updates only (e.g. typing).
  //    // Do not call from system response handlers (use merge instead).
  //
  //    logger.log('update', elId, newContent);
  //
  //    const existing: ElementState = this.get(elId) ?? this.getOrCreate(elId, newContent);
  //
  //    const newParagraphs: Paragraphs = Paragraphs.fromString(newContent, existing.seqId + 1, existing.elId);
  //
  //    const updated: ElementState = {
  //      ...existing,
  //      //      seqId: existing.seqId + 1,
  //      editState: editStateEnum.pending,
  //      //paragraphs: [newParagraph],
  //      paragraphs: newParagraphs.toDTOs(),
  //      lastUpdated: Date.now(),
  //      history: [...(existing.history ?? []), newContent],
  //    };
  //
  //    logger.log('existing state:', existing, 'updated state:', updated);
  //    this.set(updated);
  //    return updated;
  //  }

  merge(partial: Partial<documentDTO>): Document {
    // called when the user makes a change to the document by selecting a suggested correction
    // placeholder for the actual implementation
    //
    // Overlay partial fields.
    // Revalidate with documentSchema.
    // Return a new Document with merged data.
    return Document.fromString(''); // temp to satify prettier until we have a real implementation
  }

  // called when capiche responds with suggestions
  handleCapicheResponse(response: documentDTO): Document {
    const existingDocument: Document = this;
    const responseDocument: Document = Document.fromDTO(response);

    if (responseDocument.dto.srcId !== existingDocument.dto.srcId) return existingDocument;
    if ((responseDocument.dto.seqId ?? 0) < (existingDocument.dto.seqId ?? 0)) return existingDocument;

    const existingParagraphs: Paragraphs = existingDocument.getParagraphs();
    const incomingParagraphs: Paragraphs = responseDocument.getParagraphs();

    const mergedParagraphs: Paragraphs = existingParagraphs.map((existing: Paragraph) => {
      const incoming = incomingParagraphs.paragraphs.find((p: Paragraph) => p.dto.index === existing.dto.index);
      return incoming ? existing.handleCapicheResponse(incoming.toDTO()) : existing;
    });

    return new Document({
      ...existingDocument.dto,
      suggested: response.suggested ?? existingDocument.dto.suggested,
      editState: editStateEnum.checked,
      paragraphs: mergedParagraphs.toDTOs(),
    });
  }

  // previous update method from element-state-store.ts - THIS IS PROBABLY NOT RIGHT
  /** Update just part of an element state */
  //  merge(elId: string, partial: Partial<ElementState>): ElementState | undefined {
  //    const current = this.elementStates[elId];
  //    if (!current) return;
  //
  //    const merged: ElementState = {
  //      ...current,
  //      ...partial,
  //      lastUpdated: Date.now(),
  //    };
  //
  //    this.set(merged);
  //    return merged;
  //  }
  getParagraphs(): Paragraphs {
    return Paragraphs.fromDTOs(this.dto.paragraphs);
  }

  getParagraphCount(): number {
    return this.dto.paragraphs.length;
  }

  getTotalSentenceCount(): number {
    return this.getParagraphs().getTotalSentenceCount();
  }

  getTotalTokenCount(): number {
    return this.getParagraphs().getTotalTokenCount();
  }

  hasCorrectedParagraphs(): boolean {
    return this.getParagraphs().getCorrectedParagraphs().paragraphs.length > 0;
  }

  hasMisspelledTokens(): boolean {
    return this.getParagraphs().hasMisspelledTokens();
  }

  withEditState(state: editStateEnum): Document {
    return new Document({
      ...this.dto,
      editState: state,
    });
  }

  withParagraphs(paragraphs: Paragraphs): Document {
    return new Document({
      ...this.dto,
      paragraphs: paragraphs.toDTOs(),
    });
  }

  filterOnEditState(retainStates: editStateEnum[]): Document {
    return this.withParagraphs(this.getParagraphs().filterOnEditState(retainStates));
  }
}
